import { fmtCurrency, fmtNumber, fmtDate } from "@/lib/format";

// Renders a print-optimised PO document in a new window using ONLY backend
// values (no totals recalculated here) plus the Company Profile for branding.
export function printPurchaseOrder(po, company = {}) {
  const items = po.items || po.lines || [];
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const logo = company.hasLogo && company.logoDataUri ? `<img src="${company.logoDataUri}" style="height:60px"/>` : "";
  const sign = company.hasSignature && company.signatureDataUri ? `<img src="${company.signatureDataUri}" style="height:50px"/>` : "";
  const stamp = company.hasStamp && company.stampDataUri ? `<img src="${company.stampDataUri}" style="height:70px;opacity:.85"/>` : "";
  const deliveryNote = (po.notes || "").match(/Expected Delivery:\s*([0-9-]+)/);

  const rows = items.map((it, i) => `
    <tr>
      <td style="text-align:center">${i + 1}</td>
      <td>${esc(it.name || it.ingredientName || it.productName || "")}</td>
      <td style="text-align:right">${fmtNumber(it.qty ?? it.quantity)}</td>
      <td style="text-align:center">${esc(it.unit || "")}</td>
      <td style="text-align:right">${fmtCurrency(it.rate)}</td>
      <td style="text-align:right">${it.gstPct ? it.gstPct + "%" : "—"}</td>
      <td style="text-align:right">${fmtCurrency(it.amount ?? (Number(it.qty || it.quantity || 0) * Number(it.rate || 0)))}</td>
    </tr>`).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"/><title>${esc(po.poNumber || "Purchase Order")}</title>
  <style>
    *{font-family:'Segoe UI',Arial,sans-serif;box-sizing:border-box}
    body{margin:0;padding:32px;color:#1c1917;font-size:13px}
    .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #b45309;padding-bottom:14px}
    .co h1{margin:0;font-size:20px;color:#b45309}
    .co p{margin:2px 0;color:#57534e;font-size:11px}
    .title{text-align:right}
    .title h2{margin:0;font-size:22px;letter-spacing:2px;color:#292524}
    .meta{display:flex;justify-content:space-between;margin-top:16px;gap:20px}
    .box{border:1px solid #e7e5e4;border-radius:8px;padding:10px 12px;flex:1}
    .box b{display:block;color:#b45309;font-size:10px;text-transform:uppercase;margin-bottom:4px}
    table{width:100%;border-collapse:collapse;margin-top:16px}
    th{background:#faf6f0;text-align:left;padding:8px;font-size:10px;text-transform:uppercase;color:#78716c;border-bottom:2px solid #e7e5e4}
    td{padding:7px 8px;border-bottom:1px solid #f5f5f4}
    .totals{margin-top:12px;margin-left:auto;width:280px}
    .totals div{display:flex;justify-content:space-between;padding:4px 0}
    .totals .grand{border-top:2px solid #b45309;margin-top:6px;padding-top:8px;font-size:15px;font-weight:700;color:#b45309}
    .terms{margin-top:20px;font-size:11px;color:#57534e;white-space:pre-line}
    .sign{display:flex;justify-content:flex-end;gap:30px;margin-top:36px;text-align:center}
    @media print{body{padding:12px}}
  </style></head><body>
    <div class="head">
      <div class="co">${logo}<h1>${esc(company.tradeName || company.legalName || "Company")}</h1>
        <p>${esc(company.legalName || "")}</p>
        <p>${esc(company.registeredAddress || "")}</p>
        <p>GSTIN: ${esc(company.gstin || "—")} · PAN: ${esc(company.pan || "—")}</p>
        <p>FSSAI: ${esc(company.fssaiLicenceNo || "—")} · ${esc(company.email || "")} · ${esc(company.phone || "")}</p>
      </div>
      <div class="title"><h2>PURCHASE ORDER</h2>
        <p><b>${esc(po.poNumber || "")}</b></p>
        <p>Date: ${po.createdAt ? fmtDate(po.createdAt) : "—"}</p>
        ${deliveryNote ? `<p>Expected Delivery: ${esc(deliveryNote[1])}</p>` : ""}
      </div>
    </div>
    <div class="meta">
      <div class="box"><b>Supplier</b>${esc(po.supplierName || "")}<br/>${esc(po.supplierAddress || "")}<br/>GSTIN: ${esc(po.supplierGstin || "—")}</div>
      <div class="box"><b>Payment Terms</b>${esc(po.paymentTerms || "—")}<br/><b style="margin-top:6px">Status</b>${esc(po.status || "")}</div>
    </div>
    <table><thead><tr><th>#</th><th>Material</th><th style="text-align:right">Qty</th><th style="text-align:center">Unit</th><th style="text-align:right">Rate</th><th style="text-align:right">GST</th><th style="text-align:right">Amount</th></tr></thead>
      <tbody>${rows || `<tr><td colspan="7" style="text-align:center;color:#a8a29e">No line items</td></tr>`}</tbody></table>
    <div class="totals">
      <div><span>Subtotal</span><span>${fmtCurrency(po.subtotal ?? po.subTotal)}</span></div>
      <div><span>Tax</span><span>${fmtCurrency(po.taxAmount ?? po.gstAmount ?? po.totalTax)}</span></div>
      <div class="grand"><span>Grand Total</span><span>${fmtCurrency(po.grandTotal ?? po.totalAmount)}</span></div>
    </div>
    <div class="terms"><b>Terms &amp; Conditions</b>\n${esc(company.poTerms || po.notes || "")}</div>
    <div class="sign">
      <div>${stamp}<div style="border-top:1px solid #a8a29e;margin-top:6px;padding-top:4px">Company Stamp</div></div>
      <div>${sign}<div style="border-top:1px solid #a8a29e;margin-top:6px;padding-top:4px">${esc(company.authorizedSignatory || "Authorised Signatory")}</div></div>
    </div>
    <script>window.onload=()=>{window.print()}</script>
  </body></html>`;

  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  return true;
}
